import {
  Container,
  Stack,
  Box,
  Text,
  Heading,
  Divider,
  HStack,
  Card,
  CardBody,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Tab,
  useColorModeValue,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import t from "../translations";

const MotionBox = motion(Box);

function LearningCard({ entry, color, index }) {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });
  const cardBg = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.600", "gray.400");
  const navigate = useNavigate();

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1, ease: "easeOut" }}
      whileHover={{ y: -4 }}
      style={{ borderRadius: "var(--chakra-radii-xl)" }}
    >
      <Card
        bg={cardBg}
        borderRadius="xl"
        _hover={{
          boxShadow: `0 0 28px 6px var(--chakra-colors-${color}-400)`,
        }}
        transition="box-shadow 0.3s ease"
        cursor="pointer"
        onClick={() => entry.expanded ? navigate(`/learnings/${entry.slug}`) : undefined}
      >
        <CardBody>
          <Stack spacing={3}>
            <Heading size="sm" color={`${color}.400`}>
              {entry.title}
            </Heading>
            <Text color={textColor} fontSize="sm" lineHeight={1.8}>
              {entry.body}
            </Text>
            {entry.expanded && (
              <Text
                fontSize="xs"
                fontWeight={600}
                color={`${color}.400`}
                cursor="pointer"
                _hover={{ textDecoration: "underline" }}
                onClick={() => navigate(`/learnings/${entry.slug}`)}
                mt={1}
              >
                {t.learnings.readMore}
              </Text>
            )}
          </Stack>
        </CardBody>
      </Card>
    </motion.div>
  );
}

export default function Learnings() {
  const location = useLocation();
  const [tabIndex, setTabIndex] = useState(location.state?.tabIndex ?? 0);
  const textColor = useColorModeValue("gray.600", "gray.400");
  const tabBg = useColorModeValue("gray.100", "gray.700");
  const selectedBg = useColorModeValue("white", "gray.800");

  useEffect(() => {
    if (location.state?.tabIndex != null) {
      setTabIndex(location.state.tabIndex);
    }
  }, [location.state]);

  useEffect(() => {
    document.title = t.learnings.pageTitle;
  }, []);

  return (
    <Container maxW="4xl" pt={{ base: 20, md: 28 }} pb={{ base: 20, md: 36 }}>
      <Stack spacing={{ base: 8, md: 10 }}>
        <Stack align="center" direction="row" px={4}>
          <HStack mx={4}>
            <Text fontWeight={800} fontSize={{ base: "xl", md: "2xl" }} whiteSpace="nowrap">
              {t.learnings.heading}
            </Text>
          </HStack>
          <Divider />
        </Stack>

        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
          px={4}
        >
          <Text color={textColor} fontSize="lg" lineHeight={1.9}>
            {t.learnings.intro}
          </Text>
        </MotionBox>

        <Tabs variant="unstyled" isLazy index={tabIndex} onChange={setTabIndex}>
          <TabList justifyContent="center" gap={2} px={{ base: 2, md: 4 }} flexWrap="wrap">
            {t.learnings.categories.map((cat) => (
              <Tab
                key={cat.label}
                fontWeight={600}
                fontSize={{ base: "xs", md: "sm" }}
                px={{ base: 3, md: 6 }}
                py={2}
                borderRadius="full"
                bg={tabBg}
                color="gray.500"
                _selected={{
                  bg: selectedBg,
                  color: `${cat.color}.400`,
                  boxShadow: `0 0 14px 2px var(--chakra-colors-${cat.color}-400)`,
                }}
                transition="all 0.2s"
              >
                {cat.label}
              </Tab>
            ))}
          </TabList>

          <TabPanels mt={8}>
            {t.learnings.categories.map((cat) => (
              <TabPanel key={cat.label} px={0}>
                <Stack px={4} spacing={4}>
                  {cat.entries.map((entry, i) => (
                    <LearningCard
                      key={entry.title}
                      entry={entry}
                      color={cat.color}
                      index={i}
                    />
                  ))}
                </Stack>
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </Stack>
    </Container>
  );
}
